/*
  GA4 client/session id + first-touch attribution → Shopify cart attributes (#144 / GA4-E2-03).

  WHY THIS EXISTS
  The server-side purchase event (#145) fires from a backend that has no access to the browser's
  `_ga` cookie. Without the original client_id, every order it recovers lands in GA4 as a brand-new
  Direct session — we would fix the 41.7% count (#142) and destroy channel reporting in the same
  release. This carries the browser's identifiers through the cart into the order, where the
  orders/create webhook can read them back out of `note_attributes`.

  WHAT IT WRITES (all underscore-prefixed, so Shopify hides them from the customer-facing order
  summary — they are plumbing, not something a shopper should see on their confirmation):

    _ga_client_id      GA4 client id, from the `_ga` cookie
    _ga_session_id     GA4 session id, from the `_ga_<measurement>` cookie
    _ga_id_source      "cookie" | "fallback"  — see the no-cookie case below
    _attr_source       first-touch utm_source
    _attr_medium       first-touch utm_medium
    _attr_campaign     first-touch utm_campaign
    _attr_content      first-touch utm_content
    _attr_term         first-touch utm_term (#922)
    _attr_gclid        first-touch gclid
    _attr_qs           first-touch RAW query string, capped 512 (#922) — gbraid / wbraid / fbclid and
                       any parameter nobody anticipated are recoverable from it
    _attr_ref          first-touch referrer HOST ONLY, never the full referrer URL (#922)
    _attr_landing      first-touch landing path
    _attr_ts           first-touch timestamp (ISO)
    _lt_touch          the LAST tagged arrival (#887) as ONE JSON value (#931):
                       {"source","medium","campaign","content","term","gclid","qs","ref","landing","ts"}
                       in that order, "" for a field the touch lacks. Replaced whole on every visit
                       that carries attribution, untouched by an untagged visit. First touch answers
                       "where did they originally come from"; last touch answers "which message
                       brought them back this time" (win-back campaigns). Until #931 these were ten
                       `_lt_<field>` keys; they could not be cleared one by one (Shopify does not
                       store an empty value), so a shorter touch blended with the one before it.
                       They are now sent empty, which removes them from older carts.

  PAID CLICKS (#931). A click id (gclid, gbraid, wbraid, fbclid) with no utm_source / utm_medium /
  utm_campaign names its own channel: "google" / "cpc", or "facebook" / "paid-social" for fbclid. This
  runs BEFORE the referrer rule, so a paid click arriving from google.com is never booked as organic.

  REFERRER (#922). With no utm_source / utm_medium / utm_campaign and no click id, the referrer host
  becomes `source` and `medium` is "organic" for a known search engine, else "referral". A same-site
  referrer is ignored. No UTM and no referrer is a direct arrival: NOTHING is written, matching the app.
  gbraid / wbraid / fbclid get no key of their own; their presence marks the arrival as tagged (they
  replace gclid on iOS under ATT) and the values stay recoverable from `_qs`.

  PARITY WITH OM-Mobile-App (#820): same key names, same meaning, same tagged rule. The one deliberate
  difference is `_attr_ref` / `_lt_ref`: referrer HOST here, open CHANNEL on the app (a deep link has
  no referrer).

  FIRST-TOUCH FOR _attr_*. The GA4 session can expire while a cart sits for a day; #145 needs to
  attribute the order even then. First touch is stored once in localStorage and never overwritten,
  so a shopper who arrives from a campaign, leaves, and returns direct is still credited to the
  campaign rather than to `(direct)`.

  NO PII. Only identifiers and campaign values — never email, phone, name or address. Cart
  attributes travel to the order and into any downstream export, so this is a hard line
  (CLAUDE.md rule 5, and the tracking plan's "do not do this").

  NOT dependent on GTM. It reads the `_ga` cookie, which is domain-level and written by whichever
  GA4 instance is running — today the Google & YouTube app pixel, tomorrow the GTM config tag
  (#138). Both write the same client id, so this works before and after that migration and needs no
  change when it happens.
*/
(function () {
  "use strict";

  var MEASUREMENT_ID = "G-MHKC4DNNH3";
  var SESSION_COOKIE = "_ga_" + MEASUREMENT_ID.replace(/^G-/, "");
  var FIRST_TOUCH_KEY = "om_first_touch";
  var LAST_TOUCH_KEY = "om_last_touch";
  var SYNCED_KEY = "om_attr_synced";

  function cookie(name) {
    var m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return m ? m.pop() : "";
  }

  /* `_ga` is `GA1.1.<client_id>` — the client id is everything after the second dot, and it
     contains a dot itself (`1234567890.1699999999`), so split on the first two only. */
  function clientId() {
    var raw = cookie("_ga");
    if (!raw) return "";
    var parts = raw.split(".");
    return parts.length >= 4 ? parts.slice(2).join(".") : "";
  }

  /* `_ga_<id>` has had two shapes, and GS2 changed the STRUCTURE, not just the prefix:

       GS1   GS1.1.<session_id>.<n>.<...>          dot-delimited, session id is field 3
       GS2   GS2.1.s<session_id>$o1$g1$t<...>$h0   field 3 is a `$`-delimited blob, session id
                                                   is its first part, `s`-prefixed

     So field 3 has to be unpacked rather than used as-is — reading it positionally on a live GS2
     cookie returns the whole `$` blob and writes a malformed session id into the cart, which then
     joins to nothing in GA4. Handles both forms: GS1's field 3 has no `$` and no `s` prefix. */
  function sessionId() {
    var raw = cookie(SESSION_COOKIE);
    if (!raw) return "";
    var parts = raw.split(".");
    if (parts.length < 3) return "";
    var field = parts[2].split("$")[0];
    return field.charAt(0) === "s" ? field.slice(1) : field;
  }

  function store(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* private mode — degrade, don't throw */ }
  }
  function read(key) {
    try { return localStorage.getItem(key) || ""; } catch (e) { return ""; }
  }
  function clear(key) {
    try { localStorage.removeItem(key); } catch (e) { /* private mode — degrade, don't throw */ }
  }

  /* Consent declined, cookies blocked, or GA4 simply hasn't written yet. Dropping the order from
     the server-side event entirely would be worse than attributing it imperfectly, so mint a
     stable pseudo id in the GA4 client-id shape and FLAG it, so these can be counted separately
     and excluded from any analysis that needs a real GA4 join (#144 acceptance criterion 6). */
  function fallbackClientId() {
    var existing = read("om_pseudo_cid");
    if (existing) return existing;
    var rand = Math.floor(Math.random() * 1e10);
    var secs = Math.floor(Date.now() / 1000);
    var id = rand + "." + secs;
    store("om_pseudo_cid", id);
    return id;
  }

  /* Same normalisation as the app (cart_attribution.dart, OM-Docs#75): drop control characters,
     trim, cap at 200. Without it the same link stores different values on web and app, and a report
     grouping by `_lt_campaign` splits one campaign into two rows (#889). */
  var MAX_VALUE_LENGTH = 200;
  function cleanTo(value, max) {
    var s = String(value || "").replace(/[\u0000-\u001f\u007f-\u009f]/g, "").trim();
    return s.length > max ? s.substring(0, max) : s;
  }
  function clean(value) { return cleanTo(value, MAX_VALUE_LENGTH); }

  var SEARCH_ENGINE = /(^|\.)(google|bing|yahoo|duckduckgo|baidu|yandex|ecosia|ask|brave|startpage)\./;

  /* Referrer HOST only, and only when it is another site: an internal page view carries our own host
     and would otherwise overwrite a real campaign with "organicmandya.com / referral". */
  function referrerHost() {
    var ref = (document && document.referrer) || "";
    var m = ref.match(/^https?:\/\/([^\/?#:]+)/i);
    if (!m) return "";
    var host = m[1].toLowerCase().replace(/^www\./, "");
    var own = String((window.location && window.location.hostname) || "").toLowerCase().replace(/^www\./, "");
    return host === own ? "" : cleanTo(host, 128);
  }

  /* The touch described by the current URL. */
  function currentTouch() {
    var search = window.location.search || "";
    var q = new URLSearchParams(search);
    var touch = {
      source: clean(q.get("utm_source")),
      medium: clean(q.get("utm_medium")),
      campaign: clean(q.get("utm_campaign")),
      content: clean(q.get("utm_content")),
      term: clean(q.get("utm_term")),
      gclid: clean(q.get("gclid")),
      qs: cleanTo(search.replace(/^\?/, ""), 512),
      ref: referrerHost(),
      /* Cleaned, like the app's `pick(k).isNotEmpty`, so a whitespace-only id counts on neither side. */
      click: !!(clean(q.get("gbraid")) || clean(q.get("wbraid")) || clean(q.get("fbclid"))),
      landing: clean(window.location.pathname),
      ts: new Date().toISOString(),
    };
    /* #931: a click id with no UTM is a PAID click. Name its channel here, BEFORE the referrer rule,
       or a Google ad click arriving from google.com is booked as free search ("organic") and an
       fbclid from facebook.com as "referral". gclid counts too, not just the iOS ids. A real UTM
       still wins: this only runs when source, medium and campaign are all empty. Same rule as the
       app's `CartAttribution.touchFrom`. */
    if ((touch.click || touch.gclid) && !touch.source && !touch.medium && !touch.campaign) {
      var fb = !!clean(q.get("fbclid"));
      touch.source = fb ? "facebook" : "google";
      touch.medium = fb ? "paid-social" : "cpc";
    }
    if (touch.ref && !touch.source && !touch.medium && !touch.campaign) {
      touch.source = touch.ref;
      touch.medium = SEARCH_ENGINE.test(touch.ref + ".") ? "organic" : "referral";
    }
    return touch;
  }

  function carriesAttribution(touch) {
    return !!(touch && (touch.source || touch.medium || touch.campaign || touch.gclid || touch.click));
  }

  function parse(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; /* corrupted — treat as absent */ }
  }

  var touchCache = null;
  function firstTouch() {
    if (touchCache) return touchCache;
    var saved = parse(read(FIRST_TOUCH_KEY));
    if (saved) { touchCache = saved; return touchCache; }

    var touch = currentTouch();

    /* Only persist a touch that actually carries attribution, or the first page of a direct visit
       would permanently occupy the first-touch slot and lock out the campaign click that follows. */
    if (carriesAttribution(touch)) store(FIRST_TOUCH_KEY, JSON.stringify(touch));
    touchCache = touch;
    return touch;
  }

  /* Last touch (#887): overwritten on every tagged arrival, same guard as first touch so a direct
     visit never wipes a real campaign. An untagged page view returns whatever is stored. */
  var lastTouchCache = null;
  function lastTouch() {
    var touch = currentTouch();
    if (carriesAttribution(touch)) {
      store(LAST_TOUCH_KEY, JSON.stringify(touch));
      lastTouchCache = touch;
      return touch;
    }
    if (lastTouchCache) return lastTouchCache;
    lastTouchCache = parse(read(LAST_TOUCH_KEY)) || {};
    return lastTouchCache;
  }

  function attributes() {
    var cid = clientId();
    var touch = firstTouch();
    var last = lastTouch();

    var attrs = {
      _ga_client_id: cid || fallbackClientId(),
      _ga_session_id: sessionId(),
      _ga_id_source: cid ? "cookie" : "fallback",
    };
    /* No attribution means no keys at all (#922) — a direct arrival must not leave a landing-only row.
       First touch keeps its ten keys: it is written once and never replaced, so nothing can blend. */
    if (carriesAttribution(touch)) {
      TOUCH_FIELDS.forEach(function (f) { attrs["_attr_" + f] = touch[f] || ""; });
    }
    /* #931: last touch is ONE value, `_lt_touch`, holding the whole touch as JSON. As ten keys, a
       tagged arrival with fewer fields than the previous one could not clear the old ones (Shopify
       does not store an empty value), so `_lt_*` became a blend of two touches that never happened
       together. One value is replaced whole or not at all. The pre-#931 `_lt_<field>` keys are sent
       empty alongside it, which removes them from a cart that still carries them. */
    if (carriesAttribution(last)) {
      attrs._lt_touch = touchJson(last);
      TOUCH_FIELDS.forEach(function (f) { attrs["_lt_" + f] = ""; });
    }
    return attrs;
  }

  /* The fields of a touch, in the order both surfaces write them. `_lt_touch` must be byte-identical
     on web and app, so the order is fixed here and in the app's `CampaignTouch.toLastTouchJson`. */
  var TOUCH_FIELDS = ["source", "medium", "campaign", "content", "term", "gclid", "qs", "ref", "landing", "ts"];
  function touchJson(t) {
    var o = {};
    TOUCH_FIELDS.forEach(function (f) { o[f] = (t && t[f]) || ""; });
    return JSON.stringify(o);
  }

  /* What identifies a last touch for the "already synced?" checks: every field but `ts` (new on each
     tagged load, so a refresh would look like a new touch) and `landing` (as before #931). */
  function touchIdentity(json) {
    var t = parse(json) || {};
    return ["source", "medium", "campaign", "content", "term", "gclid", "qs", "ref"]
      .map(function (f) { return t[f] || ""; }).join("|");
  }

  /* A signature of what we would write, so a cart that already carries these values doesn't get a
     redundant POST on every page view. The session id changes when GA4 rolls the session, which is
     exactly when we DO want to re-sync. The last touch is part of it (#887): a second campaign click
     inside the same GA4 session must still reach the cart. Its `ts` and `landing` are left out on
     purpose (touchIdentity): `ts` is new on every tagged load, so including it would re-POST on a
     refresh or a second tap of the same link. The campaign fields identify the touch. */
  function signature(attrs) {
    return [attrs._ga_client_id, attrs._ga_session_id, touchIdentity(attrs._lt_touch)].join("|");
  }

  /* Does the cart already carry every non-empty value we meant to write? Shopify drops empty
     attribute values, so only the non-empty ones can be compared. `_lt_touch` is compared by
     touchIdentity, for the same reason as signature(): its `ts` is new on every tagged load, so two
     overlapping syncs (page load + add-to-cart) would each see the other's timestamp, call the write
     lost and retry. */
  function cartHas(cart, attrs) {
    var have = (cart && cart.attributes) || {};
    return Object.keys(attrs).every(function (k) {
      if (!attrs[k]) return true;
      if (k === "_lt_touch") return !!have[k] && touchIdentity(have[k]) === touchIdentity(attrs[k]);
      return have[k] === attrs[k];
    });
  }

  /* Another script's /cart/update.js (WebEngage) can land at the same moment and win the race, so a
     200 from our POST does not prove the values stuck. Read the cart back and only then remember the
     signature; otherwise try again a couple of times. If the check itself fails, leave the signature
     unset so the next page view retries. */
  var MAX_ATTEMPTS = 3;
  function sync(force, attempt) {
    attempt = attempt || 1;
    var attrs = attributes();
    var sig = signature(attrs);
    if (!force && attempt === 1 && read(SYNCED_KEY) === sig) return Promise.resolve();

    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: attrs }),
    })
      .then(function (res) {
        if (!res.ok) return null;
        return fetch("/cart.js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (!cart) return null;
            if (cartHas(cart, attrs)) { store(SYNCED_KEY, sig); return null; }
            if (attempt >= MAX_ATTEMPTS) return null;
            return new Promise(function (resolve) { setTimeout(resolve, 400 * attempt); })
              .then(function () { return sync(true, attempt + 1); });
          });
      })
      .catch(function () {
        /* Attribution is best-effort. A failed cart update must never surface to the shopper or
           block an add-to-cart — the order still completes, it just lands less well attributed. */
      });
  }

  /* Capture first touch immediately, even if nothing is ever added to the cart — the campaign
     parameters are on the URL now and will be gone by the time a cart exists. */
  firstTouch();
  lastTouch();

  /* Write to the cart when there is a cart to write to. Shopify's cart endpoints are shared by
     every add-to-cart path in this theme (there are several), so hook the response rather than
     each call site: any successful /cart/add.js means a cart now exists. */
  /* `origFetch` is applied to `window`, never to `this`. A detached call (`var f = window.fetch;
     f(url)`) or a bare `fetch` from bundled/strict code arrives with `this` undefined, and
     `fetch.apply(undefined, …)` throws `Illegal invocation` — which would break that add-to-cart or
     API path site-wide, for an attribution feature that is supposed to be best-effort. */
  var origFetch = window.fetch;
  if (typeof origFetch === "function") {
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isAdd = url.indexOf("/cart/add") !== -1;
      return origFetch.apply(window, arguments).then(function (res) {
        if (isAdd && res.ok) sync(false);
        return res;
      });
    };
  }

  /* A cart resumed in a later session: the cart survives, the GA4 session id does not, so refresh
     on load whenever the cart is non-empty (#144 step 3). */
  document.addEventListener("DOMContentLoaded", function () {
    /* No `cart` cookie means Shopify has never opened a cart for this browser, so there is nothing
       to read and nothing to attribute — skip the request rather than firing it on every page load
       of every session, including the ones that never shop. */
    if (!cookie("cart")) return;

    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        if (cart.item_count > 0) {
          sync(false);
          return;
        }
        /* An empty cart is the observable end of a completed order. SYNCED_KEY is keyed on
           the signature (client, session, last touch), so without this a SECOND order inside the
           same 30-minute GA4 session
           matches the stored signature, `sync()` short-circuits, and the fresh cart carries no
           attributes at all — #145's webhook then reads empty `note_attributes` and the order lands
           as (direct), which is the precise failure this file exists to prevent. */
        clear(SYNCED_KEY);
      })
      .catch(function () { /* no cart, or offline — nothing to attribute yet */ });
  });
})();
